import arraySort from 'array-sort';
import {
  ContentType,
  Publication,
  SearchPublicationsResponse,
} from '@amzn/awscat-pattern-library-service-model';
import {
  Alert,
  Box,
  Grid,
  Header,
  Input,
  Spinner,
  StatusIndicator,
} from '@amzn/awsui-components-core';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { IMplServiceClient } from '../../api/IMplServiceClient';
import { MplServiceClientFactory } from '../../api/MplServiceClientFactory';
import { withAppContext } from '../../contexts/AppContext';
import { log } from '../../loggers/Logger';
import '../../resources/styles/scss/components/pages/HomePage.css';
import { Filters } from './ContentFilters';
import NewPublicationFilters from './NewPublicationFilters';
import LatestUpdateBar from './LatestUpdateBar';
import { decodeSearchtextString } from '../utils/Search';
import {
  HomePageBanner,
  HomePageCtx,
  HomePageCtxData,
  ContentSortDropDown,
  ContentTypeSlicer,
  VisibilityTypeSlicer,
  PublicationCardViewList,
  EmptySearchResult,
} from './content-home-page';
import ReactRouterProps from '../utils/ReactRouterProps';
import { useReportPageLoadTime } from '../../monitoring/PageLoadTimeReporter';
import { usePublicationStream } from '../v2-hooks/usePublicationStream';

const DUPLICATE_SEARCH_TIMER_DELAY = 2000;

const HomePage = (props: ReactRouterProps) => {
  const mplServiceClient: IMplServiceClient = MplServiceClientFactory.create();
  const duplicateSearchTimer = useRef(null);
  const latestSearchId = useRef(0);

  /** `publications` holds the publications received from the stream so far. */
  const {
    publications,
    isFinished: isProgressiveLoadingFinished,
    errorMessage,
  } = usePublicationStream();
  const [filteredPublications, setFilteredPublications] = useState<Publication[]>([]);
  const [visibleContentIds, setVisibleContentIds] = useState<ReadonlySet<string> | null>(null);
  const [filterOptions, setFilterOptions] = useState<Filters>({});
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchErrorMessage, setSearchErrorMessage] = useState<string | undefined>(undefined);

  const allPublications = useMemo(() => {
    const validPublications = publications.filter((publication) =>
      [ContentType.Pattern, ContentType.Guide, ContentType.Strategy].includes(
        publication.contentMetadata.contentType,
      ),
    );
    arraySort(validPublications, 'lastUpdated', { reverse: true });
    return validPublications;
  }, [publications]);

  const posts = useMemo(
    () => publications.filter((p) => p.contentMetadata.contentType === ContentType.Post),
    [publications],
  );

  const visiblePublications = useMemo(
    () =>
      visibleContentIds
        ? allPublications.filter((p) => visibleContentIds.has(p.contentId))
        : allPublications,
    [allPublications, visibleContentIds],
  );

  // When the user arrives with a search term, then run that search while the content downloads.
  useEffect(() => {
    const searchInput = decodeSearchtextString(props?.location?.state?.searchInput || '');
    if (searchInput?.length) {
      setSearchTerm(searchInput);
      loadSearchPublications(searchInput);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // @ts-expect-error TS7006 - strict migration
  const handleSearchChange = async (event) => {
    setSearchTerm(decodeSearchtextString(event.detail.value));
    // @ts-expect-error TS2769 - strict migration
    clearTimeout(duplicateSearchTimer.current);
    // @ts-expect-error TS2322 - strict migration
    duplicateSearchTimer.current = setTimeout(async () => {
      await loadSearchPublications(event.detail.value);
    }, DUPLICATE_SEARCH_TIMER_DELAY);
  };

  async function loadSearchPublications(searchInput: string) {
    // Clear the last search error, since a new search replaces it.
    setSearchErrorMessage(undefined);

    // Tag this search so an older one that finishes later cannot overwrite it.
    const searchId = ++latestSearchId.current;
    if (!searchInput.length) {
      setIsSearching(false);
      setVisibleContentIds(null);
      return;
    }

    setIsSearching(true);
    let response: SearchPublicationsResponse;
    try {
      response = await mplServiceClient.searchPublications(searchInput);
    } catch (err) {
      // Keep the previous results on failure so the page stays usable.
      log.error({ err }, 'Home page search failed');
      if (searchId === latestSearchId.current) {
        setSearchErrorMessage('Search failed. Try again.');
        setIsSearching(false);
      }
      return;
    }

    // Show matches only from the newest search, since the user moved on from older ones.
    if (searchId !== latestSearchId.current) {
      return;
    }
    setIsSearching(false);
    setVisibleContentIds(new Set(response.publications.map((p) => p.contentId)));
  }

  // @ts-expect-error TS7006 - strict migration
  const handleSearchEnterPress = async (event) => {
    // @ts-expect-error TS2769 - strict migration
    clearTimeout(duplicateSearchTimer.current);
    if (event.detail.keyCode === 13) {
      event.preventDefault();
      await loadSearchPublications(searchTerm);
    }
  };

  const homepageContextData: HomePageCtxData = {
    filteredPublications,
    setFilteredPublications,
    filterOptions,
    setFilterOptions,
    allPublications,
    searchTerm,
  };

  // Count the page as loaded once the first publication tiles show, not the spinner or the empty state.
  const isPublicationListShown = !isSearching && filteredPublications.length > 0;
  useReportPageLoadTime('home', isPublicationListShown);

  const renderPublications = () => {
    if (isSearching) {
      return <Spinner />;
    }
    if (filteredPublications.length) {
      return <PublicationCardViewList isLoadingMore={!isProgressiveLoadingFinished} />;
    }
    if (!isProgressiveLoadingFinished) {
      return <StatusIndicator type="loading">Loading contents...</StatusIndicator>;
    }
    return <EmptySearchResult />;
  };

  const displayFilterCount = () => {
    // The total keeps growing while content arrives, so show only what is listed until it ends.
    if (!isProgressiveLoadingFinished) {
      return `${filteredPublications.length}, loading contents...`;
    }
    if (filteredPublications.length === allPublications.length) {
      return allPublications.length;
    }
    return `${filteredPublications.length} of ${allPublications.length}`;
  };

  return (
    <HomePageCtx.Provider value={homepageContextData}>
      <HomePageBanner />
      <LatestUpdateBar allPosts={posts} loading={!isProgressiveLoadingFinished} />
      <Box padding={{ top: 'l', bottom: 's', horizontal: 'l' }}>
        <Grid disableGutters gridDefinition={[{ colspan: 12 }]}>
          <Grid gridDefinition={[{ colspan: { default: 2 } }, { colspan: { default: 10 } }]}>
            <NewPublicationFilters publications={visiblePublications} />
            <Box>
              <div>
                <Box
                  variant="div"
                  padding={{ bottom: 's' }}
                  className="home-page-content-view-header"
                >
                  <div className="home-page-content-title">
                    <Header variant="h3" counter={`(${displayFilterCount()})`}>
                      Content
                    </Header>
                  </div>
                  <div className="home-page-search-container">
                    <Input
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchEnterPress}
                      placeholder="Find content"
                      type="search"
                      ariaRequired
                    />
                  </div>
                </Box>
                <Box
                  variant="div"
                  padding={{ top: 'xl' }}
                  className="home-page-content-view-filter-bar"
                >
                  <div className="home-page-content-view-slicer-container">
                    <ContentTypeSlicer />
                    <VisibilityTypeSlicer />
                  </div>
                  {/* Each new batch undoes a sort, so allow sorting once all content has arrived. */}
                  <ContentSortDropDown isDisabled={!isProgressiveLoadingFinished} />
                </Box>
              </div>
              {errorMessage && <Alert type="error">{errorMessage}</Alert>}
              {searchErrorMessage && <Alert type="error">{searchErrorMessage}</Alert>}
              {renderPublications()}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </HomePageCtx.Provider>
  );
};

export default withAppContext(HomePage);
